/**
 * Sponsors Part 4's two printed pieces.
 *
 * The banner order is what the vendor gets: sponsor · size · file · quantity · notes, one page,
 * with the ones nobody can print listed at the foot so nobody prints a blank.
 *
 * The receipt for a gift in kind reads the description and the parish's standard line, and never
 * a dollar figure — the donor values a gift in kind, not the parish.
 */
import type { ReactNode } from 'react';
import { Paper, PrintRoot, formatDate } from '@cider/ui';
import { useLoad } from '../views/admin/shared.js';

interface BannerOrder {
  festivalYear: number;
  lines: {
    organizationName: string;
    level: string | null;
    size: string | null;
    fileName: string | null;
    quantity: number | null;
    note: string | null;
  }[];
  missing: { organizationName: string; level: string | null; why: string }[];
  says: string;
}

export function BannerOrderPage({ year }: { year: string }): ReactNode {
  const order = useLoad<BannerOrder>(`/marketing/recognition/banner-order?year=${year}`);
  if (order.error) return <p role="alert">{order.error}</p>;
  const d = order.data;
  if (!d) return <p>Reading the banner order…</p>;
  return (
    <PrintRoot
      kind="letter"
      title={`Banner order — ${String(d.festivalYear)}`}
      pages={[
        <Paper
          key="1"
          masthead="Banner order"
          submast={`Saint Lawrence Apple Fest · ${String(d.festivalYear)} · ${d.says}`}
          stamp={`${String(d.lines.length)} BANNERS`}
          rows={[
            ...d.lines.map((l) => ({
              key: l.organizationName,
              a: `${l.organizationName}${l.level ? ` · ${l.level}` : ''}`,
              b: [l.size, l.fileName, l.note].filter(Boolean).join(' · '),
              c: `× ${String(l.quantity ?? 1)}`,
            })),
            ...d.missing.map((m) => ({
              key: `missing-${m.organizationName}`,
              a: `${m.organizationName}${m.level ? ` · ${m.level}` : ''}`,
              b: `Not on this order — ${m.why}`,
              c: '—',
            })),
          ]}
          foot="Every file named here is the approved original. Nothing else is sent to a printer."
        />,
      ]}
    />
  );
}

interface Receipt {
  organizationName: string;
  festivalYear: number;
  description: string;
  receivedAt: string | null;
  areaName: string | null;
  line: string;
}

export function GiftReceipt({ id }: { id: string }): ReactNode {
  const receipt = useLoad<Receipt>(`/marketing/gifts/${id}/receipt`);
  if (receipt.error) return <p role="alert">{receipt.error}</p>;
  const d = receipt.data;
  if (!d) return <p>Reading the gift…</p>;
  return (
    <PrintRoot
      kind="letter"
      title={`Gift receipt — ${d.organizationName}`}
      pages={[
        <Paper
          key="1"
          masthead="Receipt for a gift in kind"
          submast={`Saint Lawrence Apple Fest · ${String(d.festivalYear)} · ${d.organizationName}`}
          stamp={d.receivedAt ? formatDate(d.receivedAt, { year: true }).toUpperCase() : 'NOT YET'}
          rows={[
            { key: 'what', a: 'What was given', b: d.description },
            {
              key: 'when',
              a: 'Received',
              b: `${d.receivedAt ? formatDate(d.receivedAt) : 'not yet'}${d.areaName ? ` · ${d.areaName}` : ''}`,
            },
          ]}
          foot="Saint Lawrence Apple Fest · Saint Lawrence Parish, Utica, Michigan. With thanks from the festival and the school."
        >
          <div className="paper-letter">
            <p>{d.line}</p>
          </div>
        </Paper>,
      ]}
    />
  );
}

interface SponsorList {
  festivalYear: number;
  which: 'program' | 'wall';
  levels: { level: string; names: string[] }[];
  says: string;
}

/**
 * The program's sponsor page and the sponsor wall list (Track 3, 1e): every sponsor whose level
 * earns the placement, by level, the biggest first — what the printer sets and what the wall
 * crew hangs. The mark at the top; names only, never an amount.
 */
export function SponsorListPage({
  year,
  which,
}: {
  year: string;
  which: 'program' | 'wall';
}): ReactNode {
  const list = useLoad<SponsorList>(
    `/marketing/recognition/sponsor-list?year=${year}&which=${which}`,
  );
  if (list.error) return <p role="alert">{list.error}</p>;
  const d = list.data;
  if (!d) return <p>Reading the sponsors…</p>;
  const title = which === 'program' ? 'Our sponsors — the program page' : 'The sponsor wall';
  return (
    <PrintRoot
      kind="letter"
      title={`${title} — ${String(d.festivalYear)}`}
      pages={[
        <Paper
          key="1"
          masthead={which === 'program' ? 'Thank you to our sponsors' : 'The sponsor wall'}
          submast={`Saint Lawrence Apple Fest · ${String(d.festivalYear)} · ${d.says}`}
        >
          <div className="paper-letter">
            {d.levels.map((l) => (
              <section key={l.level}>
                <h2>{l.level}</h2>
                <p className="sheet__names">{l.names.join(' · ')}</p>
              </section>
            ))}
          </div>
        </Paper>,
      ]}
    />
  );
}
